import { useTranslation } from "react-i18next";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { BrutalCard } from "@/components/ui/BrutalCard";
import { SEO } from "@/components/SEO";
import { SEOBreadcrumb } from "@/components/SEOBreadcrumb";

export default function Privacy() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen flex flex-col">
      <SEO 
        title={t('legal.privacy.title')}
        description={t('legal.privacy.sections.intro.content')}
      />
      <Header />
      
      <main className="flex-1 container mx-auto px-4 py-12">
        <BrutalCard className="p-8 max-w-4xl mx-auto">
          <SEOBreadcrumb items={[{ label: t('legal.privacy.breadcrumb') }]} align="left" />
          <h1 className="text-4xl font-heading font-bold mb-8">
            {t('legal.privacy.title')}
          </h1>
          
          <p className="text-muted-foreground mb-6">
            {t('legal.privacy.lastUpdated')}: {t('legal.privacy.effectiveDate')}
          </p>
          
          <div className="prose prose-lg max-w-none space-y-8">
            {/* 1. Introduzione */}
            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">
                {t('legal.privacy.sections.intro.title')}
              </h2>
              <p>{t('legal.privacy.sections.intro.content')}</p>
            </section>

            {/* 2. Titolare del trattamento */}
            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">
                {t('legal.privacy.sections.dataController.title')}
              </h2>
              <div 
                className="whitespace-pre-line"
                dangerouslySetInnerHTML={{ 
                  __html: t('legal.privacy.sections.dataController.content') 
                }}
              />
            </section>

            {/* 3. Dati raccolti */}
            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">
                {t('legal.privacy.sections.dataCollection.title')}
              </h2>
              
              <h3 className="text-xl font-heading font-semibold mt-6 mb-3">
                {t('legal.privacy.sections.dataCollection.subtitle1')}
              </h3>
              <div 
                className="whitespace-pre-line"
                dangerouslySetInnerHTML={{ 
                  __html: t('legal.privacy.sections.dataCollection.content1') 
                }}
              />
              
              <h3 className="text-xl font-heading font-semibold mt-6 mb-3">
                {t('legal.privacy.sections.dataCollection.subtitle2')}
              </h3>
              <div 
                className="whitespace-pre-line"
                dangerouslySetInnerHTML={{ 
                  __html: t('legal.privacy.sections.dataCollection.content2') 
                }}
              />
              
              <h3 className="text-xl font-heading font-semibold mt-6 mb-3">
                {t('legal.privacy.sections.dataCollection.subtitle3')}
              </h3>
              <div 
                className="whitespace-pre-line"
                dangerouslySetInnerHTML={{ 
                  __html: t('legal.privacy.sections.dataCollection.content3') 
                }}
              />
            </section>

            {/* 4. Base giuridica */}
            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">
                {t('legal.privacy.sections.legalBasis.title')}
              </h2>
              <div 
                className="whitespace-pre-line"
                dangerouslySetInnerHTML={{ 
                  __html: t('legal.privacy.sections.legalBasis.content') 
                }}
              />
            </section>

            {/* 5. Finalità */}
            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">
                {t('legal.privacy.sections.dataUsage.title')}
              </h2>
              <div 
                className="whitespace-pre-line"
                dangerouslySetInnerHTML={{ 
                  __html: t('legal.privacy.sections.dataUsage.content') 
                }}
              />
            </section>

            {/* 6. Servizi terze parti */}
            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">
                {t('legal.privacy.sections.thirdParties.title')}
              </h2>
              <div 
                className="whitespace-pre-line"
                dangerouslySetInnerHTML={{ 
                  __html: t('legal.privacy.sections.thirdParties.content') 
                }}
              />
            </section>

            {/* 7. Trasferimenti internazionali */}
            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">
                {t('legal.privacy.sections.international.title')}
              </h2>
              <div 
                className="whitespace-pre-line"
                dangerouslySetInnerHTML={{ 
                  __html: t('legal.privacy.sections.international.content') 
                }}
              />
            </section>

            {/* 8. Conservazione dati */}
            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">
                {t('legal.privacy.sections.retention.title')}
              </h2>
              <div 
                className="whitespace-pre-line"
                dangerouslySetInnerHTML={{ 
                  __html: t('legal.privacy.sections.retention.content') 
                }}
              />
            </section>

            {/* 9. Diritti GDPR */}
            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">
                {t('legal.privacy.sections.userRights.title')}
              </h2>
              <div 
                className="whitespace-pre-line"
                dangerouslySetInnerHTML={{ 
                  __html: t('legal.privacy.sections.userRights.content') 
                }}
              />
            </section>

            {/* 10. Profilazione */}
            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">
                {t('legal.privacy.sections.profiling.title')}
              </h2>
              <div 
                className="whitespace-pre-line"
                dangerouslySetInnerHTML={{ 
                  __html: t('legal.privacy.sections.profiling.content') 
                }}
              />
            </section>

            {/* 11. Cookie */}
            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">
                {t('legal.privacy.sections.cookies.title')}
              </h2>
              <div 
                className="whitespace-pre-line"
                dangerouslySetInnerHTML={{ 
                  __html: t('legal.privacy.sections.cookies.content') 
                }}
              />
            </section>

            {/* 12. Sicurezza */}
            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">
                {t('legal.privacy.sections.security.title')}
              </h2>
              <div 
                className="whitespace-pre-line"
                dangerouslySetInnerHTML={{ 
                  __html: t('legal.privacy.sections.security.content') 
                }}
              />
            </section>

            {/* 13. Minori */}
            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">
                {t('legal.privacy.sections.minors.title')}
              </h2>
              <div 
                className="whitespace-pre-line"
                dangerouslySetInnerHTML={{ 
                  __html: t('legal.privacy.sections.minors.content') 
                }}
              />
            </section>

            {/* 14. Modifiche */}
            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">
                {t('legal.privacy.sections.changes.title')}
              </h2>
              <div 
                className="whitespace-pre-line"
                dangerouslySetInnerHTML={{ 
                  __html: t('legal.privacy.sections.changes.content') 
                }}
              />
            </section>

            {/* 15. Contatti */}
            <section>
              <h2 className="text-2xl font-heading font-bold mb-4">
                {t('legal.privacy.sections.contact.title')}
              </h2>
              <p>{t('legal.privacy.sections.contact.content')}</p>
              <div className="bg-muted/50 border-2 border-dark p-5 mt-6 shadow-brutal">
                <h3 className="text-2xl font-heading font-bold mb-4 mt-0">
                  Titolare del Trattamento
                </h3>
                <p className="font-bold">Gabriele Lakhal</p>
                <p>P.IVA IT12773030965</p>
                <p className="mb-0">
                  Email:{' '}
                  <a href="mailto:gabrielelakhal@gmail.com" className="underline hover:no-underline">
                    gabrielelakhal@gmail.com
                  </a>
                </p>
              </div>
            </section>
          </div>
        </BrutalCard>
      </main>

      <Footer />
    </div>
  );
}
